import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyKeybind,
  describeKeyCode,
  DEFAULT_KEYBINDS,
  isReservedCode,
  loadKeybindPrefs,
  resetKeybindPrefs,
  saveKeybindPrefs,
  type KeybindPrefs,
  type Keybinding,
} from '../../game/keybindPrefs';
import './keybindSettings.css';

const PIANO_LANE_NAMES = ['Lane 1', 'Lane 2', 'Lane 3', 'Lane 4'] as const;
const GUITAR_LANE_NAMES = ['Green', 'Red', 'Yellow', 'Blue'] as const;

type Capture =
  | { mode: 'circles'; slot: 0 | 1 }
  | { mode: 'piano'; lane: number; slot: 0 | 1 }
  | { mode: 'guitar'; lane: number; slot: 0 | 1 };

function sameCapture(a: Capture, b: Capture): boolean {
  if (a.mode !== b.mode) {
    return false;
  }
  if (a.mode === 'circles' && b.mode === 'circles') {
    return a.slot === b.slot;
  }
  if (a.mode === 'piano' && b.mode === 'piano') {
    return a.lane === b.lane && a.slot === b.slot;
  }
  if (a.mode === 'guitar' && b.mode === 'guitar') {
    return a.lane === b.lane && a.slot === b.slot;
  }
  return false;
}


/**
 * Per-mode key binding editor. Each slot is a button that, when clicked,
 * enters capture mode and listens for the next non-reserved keydown.
 * Bindings are persisted to localStorage on commit and picked up by
 * the input sources on the next session start (the in-game Restart
 * button applies them immediately).
 *
 * Captures use {@link KeyboardEvent.code} so a player on AZERTY who
 * binds the physical Z key gets the same key regardless of layout —
 * matching how the input sources resolve presses.
 */
export function KeybindSettings() {
  const [prefs, setPrefs] = useState<KeybindPrefs>(() => loadKeybindPrefs());
  const [capture, setCapture] = useState<Capture | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const captureRef = useRef<Capture | null>(null);
  captureRef.current = capture;

  const commit = useCallback((next: KeybindPrefs) => {
    setPrefs(next);
    saveKeybindPrefs(next);
  }, []);

  const cancelCapture = useCallback(() => {
    setCapture(null);
    setCaptureError(null);
  }, []);

  // Single global keydown listener while capture is active. We rely on
  // a ref to read the latest capture target so the listener doesn't
  // need to re-subscribe on every capture change (and so callers can
  // start capture without flicker).
  useEffect(() => {
    if (!capture) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      const target = captureRef.current;
      if (!target) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setCapture(null);
        setCaptureError(null);
        return;
      }
      if (isReservedCode(e.code)) {
        e.preventDefault();
        setCaptureError(`${describeKeyCode(e.code)} is reserved — pick another key.`);
        return;
      }
      e.preventDefault();
      setCaptureError(null);
      setCapture(null);
      commit(applyKeybind(prefs, target, e.code));
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [capture, commit, prefs]);

  const startCapture = useCallback(
    (target: Capture) => {
      if (capture && sameCapture(capture, target)) {
        cancelCapture();
        return;
      }
      setCaptureError(null);
      setCapture(target);
    },
    [capture, cancelCapture],
  );

  const restoreDefaults = useCallback(() => {
    setCapture(null);
    setCaptureError(null);
    setPrefs(resetKeybindPrefs());
  }, []);

  return (
    <div className="card" data-testid="keybind-settings">
      <div className="keybind-header">
        <h3>Keybinds</h3>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={restoreDefaults}
          data-testid="keybinds-reset"
          title="Restore default Z/X, Q W E R / D F J K bindings"
        >
          Reset
        </button>
      </div>
      <p className="muted faint">
        Click a key, then press the new binding. <kbd>Esc</kbd> cancels.{' '}
        <kbd>Space</kbd>, <kbd>Esc</kbd> and <kbd>P</kbd> are reserved (P
        pauses a run). Rebinds apply on the next run — use Restart in-game
        to play with the new keys.
      </p>

      {captureError && (
        <div className="keybind-error" data-testid="keybind-error">
          {captureError}
        </div>
      )}

      <div className="keybind-section">
        <h4>Circles</h4>
        <p className="muted faint">
          Tap-at-cursor keys. <kbd>Space</kbd> and every Piano lane key also work
          as taps automatically.
        </p>
        <div className="keybind-grid keybind-grid-circles">
          {prefs.circlesTap.map((code, slot) => (
            <KeybindSlot
              key={`circles-${slot}`}
              label={`Tap ${slot + 1}`}
              code={code}
              defaultCode={DEFAULT_KEYBINDS.circlesTap[slot]}
              capturing={
                !!capture &&
                capture.mode === 'circles' &&
                capture.slot === slot
              }
              onStartCapture={() =>
                startCapture({ mode: 'circles', slot: slot as 0 | 1 })
              }
              testId={`keybind-circles-${slot}`}
            />
          ))}
        </div>
      </div>

      <div className="keybind-section">
        <h4>Piano</h4>
        <p className="muted faint">
          Two keys per lane — defaults to the top row{' '}
          <kbd>Q</kbd> <kbd>W</kbd> <kbd>E</kbd> <kbd>R</kbd> as primary
          and the home row <kbd>D</kbd> <kbd>F</kbd> <kbd>J</kbd>{' '}
          <kbd>K</kbd> as alternate. Either row works in-game.
        </p>
        <div className="keybind-grid keybind-grid-piano">
          {prefs.pianoLanes.map((laneBindings, laneIdx) => (
            <div className="keybind-lane" key={`lane-${laneIdx}`}>
              <span className="keybind-lane-label">
                {PIANO_LANE_NAMES[laneIdx] ?? `Lane ${laneIdx + 1}`}
              </span>
              <div className="keybind-lane-slots">
                {laneBindings.map((code, slot) => (
                  <KeybindSlot
                    key={`piano-${laneIdx}-${slot}`}
                    label={slot === 0 ? 'Primary' : 'Alt'}
                    code={code}
                    defaultCode={DEFAULT_KEYBINDS.pianoLanes[laneIdx][slot]}
                    capturing={
                      !!capture &&
                      capture.mode === 'piano' &&
                      capture.lane === laneIdx &&
                      capture.slot === slot
                    }
                    onStartCapture={() =>
                      startCapture({
                        mode: 'piano',
                        lane: laneIdx,
                        slot: slot as 0 | 1,
                      })
                    }
                    testId={`keybind-piano-${laneIdx}-${slot}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="keybind-section">
        <h4>Guitar</h4>
        <p className="muted faint">
          Two keys per lane — defaults to the home row{' '}
          <kbd>D</kbd> <kbd>F</kbd> <kbd>J</kbd> <kbd>K</kbd>{' '}
          as primary, with the top row <kbd>Q</kbd> <kbd>W</kbd>{' '}
          <kbd>E</kbd> <kbd>R</kbd> as alternate. Either row works in-game.
        </p>
        <div className="keybind-grid keybind-grid-piano">
          {prefs.guitarLanes.map((laneBindings, laneIdx) => (
            <div className="keybind-lane" key={`guitar-lane-${laneIdx}`}>
              <span className="keybind-lane-label">
                {GUITAR_LANE_NAMES[laneIdx] ?? `Lane ${laneIdx + 1}`}
              </span>
              <div className="keybind-lane-slots">
                {laneBindings.map((code, slot) => (
                  <KeybindSlot
                    key={`guitar-${laneIdx}-${slot}`}
                    label={slot === 0 ? 'Primary' : 'Alt'}
                    code={code}
                    defaultCode={DEFAULT_KEYBINDS.guitarLanes[laneIdx][slot]}
                    capturing={
                      !!capture &&
                      capture.mode === 'guitar' &&
                      capture.lane === laneIdx &&
                      capture.slot === slot
                    }
                    onStartCapture={() =>
                      startCapture({
                        mode: 'guitar',
                        lane: laneIdx,
                        slot: slot as 0 | 1,
                      })
                    }
                    testId={`keybind-guitar-${laneIdx}-${slot}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface KeybindSlotProps {
  label: string;
  code: Keybinding;
  defaultCode: string;
  capturing: boolean;
  onStartCapture: () => void;
  testId: string;
}

function KeybindSlot({
  label,
  code,
  defaultCode,
  capturing,
  onStartCapture,
  testId,
}: KeybindSlotProps) {
  const display = capturing ? 'Press a key…' : describeKeyCode(code);
  const isCustom = code !== defaultCode;
  return (
    <button
      type="button"
      className={`keybind-slot ${capturing ? 'is-capturing' : ''} ${isCustom ? 'is-custom' : ''}`}
      onClick={onStartCapture}
      aria-pressed={capturing}
      data-testid={testId}
      title={
        capturing
          ? 'Press the new key, or Esc to cancel'
          : `Click to rebind (default ${describeKeyCode(defaultCode)})`
      }
    >
      <span className="keybind-slot-label">{label}</span>
      <span className="keybind-slot-key">{display}</span>
    </button>
  );
}
